'use client';

/**
 * useProactiveRecommendations — Analyzes DreamTwin state and injects
 * proactive recommendations automatically. Call this once in the app.
 */

import { useEffect } from 'react';
import { useDreamTwin } from '@/lib/dreamTwin';

export function useProactiveRecommendations() {
  const { twin, addRecommendation } = useDreamTwin();

  useEffect(() => {
    if (!twin) return;

    const { marketing, website, operations, products, identity, plan } = twin;

    // Email marketing
    if (!marketing?.emailMarketing) {
      addRecommendation({
        category: 'Marketing',
        title: 'Set up email marketing',
        description: 'Email marketing has the highest ROI of any channel. Start building your list now.',
        impact: 'high',
        estimatedGain: 11,
        estimatedMinutes: 45,
        difficulty: 'Easy',
        actionUrl: '/business-knowledge-vault',
      });
    }

    // Lead magnet
    if (!marketing?.leadMagnet) {
      addRecommendation({
        category: 'Marketing',
        title: 'Create a lead magnet',
        description: 'A free resource (PDF, checklist, mini-course) accelerates your first 100 subscribers.',
        impact: 'high',
        estimatedGain: 9,
        estimatedMinutes: 60,
        difficulty: 'Medium',
        actionUrl: '/business-builder',
      });
    }

    // Website
    if (!website?.hasWebsite) {
      addRecommendation({
        category: 'Website',
        title: 'Build your website',
        description: 'A website is your digital home. Without it, you cannot convert visitors into customers.',
        impact: 'high',
        estimatedGain: 15,
        estimatedMinutes: 120,
        difficulty: 'Hard',
        actionUrl: '/dream-workspace',
      });
    }

    // Payment processor
    if (!operations?.hasStripe) {
      addRecommendation({
        category: 'Sales',
        title: 'Connect a payment processor',
        description: 'You cannot make sales without a way to accept payments. Set up Stripe to unlock revenue.',
        impact: 'high',
        estimatedGain: 12,
        estimatedMinutes: 20,
        difficulty: 'Easy',
        actionUrl: '/account',
      });
    }

    // Mission statement
    if (!identity?.mission) {
      addRecommendation({
        category: 'Vision',
        title: 'Write your mission statement',
        description: 'A clear mission guides every decision and resonates with your ideal customers.',
        impact: 'medium',
        estimatedGain: 5,
        estimatedMinutes: 15,
        difficulty: 'Easy',
        actionUrl: '/dream-workspace',
      });
    }

    // Pricing strategy
    if (!products?.pricingStrategy) {
      addRecommendation({
        category: 'Products',
        title: 'Define your pricing strategy',
        description: 'Inconsistent pricing confuses customers. A clear strategy increases perceived value.',
        impact: 'medium',
        estimatedGain: 7,
        estimatedMinutes: 30,
        difficulty: 'Medium',
        actionUrl: '/business-builder',
      });
    }

    // Email automation
    if (!operations?.hasEmailAutomation) {
      addRecommendation({
        category: 'Automation',
        title: 'Set up email automation',
        description: 'Automated welcome sequences convert subscribers into customers while you sleep.',
        impact: 'medium',
        estimatedGain: 8,
        estimatedMinutes: 45,
        difficulty: 'Medium',
        actionUrl: '/business-knowledge-vault',
      });
    }

    // SEO
    if (!marketing?.seoStrategy) {
      addRecommendation({
        category: 'Marketing',
        title: 'Develop an SEO strategy',
        description: 'Organic search traffic is free and compounds over time. Start with 5 target keywords.',
        impact: 'medium',
        estimatedGain: 6,
        estimatedMinutes: 30,
        difficulty: 'Medium',
        actionUrl: '/business-knowledge-vault',
      });
    }

    // Launch plan
    if (!plan?.launchPlan && plan?.launchStatus !== 'launched') {
      addRecommendation({
        category: 'Launch',
        title: 'Create your launch plan',
        description: 'A structured launch plan increases your chances of a successful first sale by 3x.',
        impact: 'high',
        estimatedGain: 10,
        estimatedMinutes: 60,
        difficulty: 'Medium',
        actionUrl: '/business-builder',
      });
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twin?.identity?.businessName, twin?.marketing, twin?.website, twin?.operations, twin?.products, twin?.plan]);
}
